import { Lock, Trash2, KeyRound, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { pickBg } from "@/assets/generated";
import { Reveal, RevealStagger } from "@/components/vision/reveal";
import { EditorialHeading } from "./editorial-heading";

export function SecuritySection() {
  const { t } = useTranslation();
  const pillars = [
    { icon: Lock, title: t("security.pillars.training.title"), desc: t("security.pillars.training.desc") },
    { icon: Trash2, title: t("security.pillars.retention.title"), desc: t("security.pillars.retention.desc") },
    { icon: KeyRound, title: t("security.pillars.crypto.title"), desc: t("security.pillars.crypto.desc") },
    { icon: Globe, title: t("security.pillars.eu.title"), desc: t("security.pillars.eu.desc") },
  ];
  return (
    <section id="security" className="relative overflow-hidden bg-background py-16 sm:py-28 md:py-32">
      <img src={pickBg(2)} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-15 dark:opacity-25" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="aurora-orb absolute -left-32 top-0" style={{ width: 520, height: 520, background: "radial-gradient(circle at 30% 30%, hsl(var(--aurora-2) / 0.55), transparent 60%)" }} />
        <div className="aurora-orb absolute right-[-6rem] bottom-0" style={{ width: 460, height: 460, background: "radial-gradient(circle at 30% 30%, hsl(var(--aurora-4) / 0.5), transparent 60%)", animationDelay: "4s" }} />
      </div>
      <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <EditorialHeading
          index="No. 08"
          eyebrow={t("security.eyebrow")}
          title={t("security.title")}
          subtitle={t("security.subtitle")}
        />
        <RevealStagger className="mt-10 grid gap-4 sm:mt-16 sm:gap-6 sm:grid-cols-2" stagger={0.1} y={28} blur={8}>
          {pillars.map((p) => (
            <div
              key={p.title}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 transition-all duration-500 hover:border-accent/40 hover:shadow-xl hover:-translate-y-1 sm:p-7"
            >
              <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-accent/5 transition-all duration-700 group-hover:scale-150 group-hover:bg-accent/10" />
              <div className="relative">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground sm:h-12 sm:w-12 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                  <p.icon className="h-5 w-5 sm:h-6 sm:w-6" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-foreground sm:mt-5 sm:text-xl">{p.title}</h3>
                <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground sm:text-sm">{p.desc}</p>
              </div>
            </div>
          ))}
        </RevealStagger>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-[11px] text-muted-foreground sm:mt-12 sm:gap-3 sm:text-xs">
          <span className="rounded-md border border-border bg-card px-3 py-1.5">{t("security.badges.gdpr")}</span>
          <span className="rounded-md border border-border bg-card px-3 py-1.5">{t("security.badges.aiAct")}</span>
        </div>
        <p className="mt-8 text-center font-display text-lg italic text-foreground/80 sm:mt-12 sm:text-2xl text-balance">
          {t("security.quote")}
        </p>
      </div>
    </section>
  );
}
